import { memo } from "react";

import { UserInformation, type UserInformationUser } from "@synthesis/shared-ui";
import { formatBytes } from "@synthesis/shared-utils";

import type { FileMetaDto, UserDto } from "../../../api";
import { useUserInformation } from "../hooks/use-user-information";

const scrollableContainerClassName =
  "h-[93%] w-full overflow-y-scroll [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar]:rounded-lg [&::-webkit-scrollbar-thumb]:rounded-[20px] [&::-webkit-scrollbar-thumb]:bg-[#d1d5db]";

const toUserInformationUser = (dto: UserDto | null): UserInformationUser | null => {
  if (!dto) return null;
  return {
    id: dto.id ?? undefined,
    lastName: dto.lastName ?? undefined,
    firstName: dto.firstName ?? undefined,
    middleName: dto.middleName ?? undefined,
    email: dto.email ?? undefined,
    enabled: dto.enabled ?? undefined,
    position: dto.position ?? undefined,
    groups: dto.groups ? dto.groups.map((g) => ({ id: g.id ?? undefined, name: g.name ?? undefined })) : undefined,
  };
};

type MetaDataProps = {
  info: FileMetaDto;
};

const MetaData = memo<MetaDataProps>(({ info }) => {
  const [_isLoading, user] = useUserInformation(info.author);
  const uiUser = toUserInformationUser(user);

  return (
    <main className={scrollableContainerClassName}>
      {info ? (
        <div className="my-4 px-4">
          <p className="mb-1 flex overflow-hidden overflow-ellipsis whitespace-nowrap pl-1" data-cy="metaData-text-name">
            Наименование: {info.baseName}
          </p>
          <p className="mb-1 flex overflow-hidden overflow-ellipsis whitespace-nowrap pl-1" data-cy="metaData-text-size">
            Размер: {formatBytes(info.size)}
          </p>
          <p className="mb-1 flex overflow-hidden overflow-ellipsis whitespace-nowrap pl-1" data-cy="metaData-text-type">
            Тип: {(info.type === "DOCUMENT" || info.type === "PDF_DOCUMENT") && "Документ"} ({info.ext})
          </p>
          <p className="mb-1 flex overflow-hidden overflow-ellipsis whitespace-nowrap pl-1" data-cy="metaData-text-version">
            Версия: {info.version}
          </p>
          <div data-cy="metaData-div-user" className="flex">
            Автор:
            <UserInformation
              trigger={
                <p className="mb-1 flex overflow-hidden overflow-ellipsis whitespace-nowrap pl-1" data-cy="metaData-text-user">
                  {user?.lastName} {user?.firstName} {user?.middleName}
                </p>
              }
              user={uiUser}
            />
          </div>
        </div>
      ) : (
        <div className="px-4 pt-4">
          <div className="flex flex-wrap">
            {Array.from(Array(6), (_, i) => (
              <div key={i} className="bg-grey-300 mb-3 h-[20px] w-full animate-pulse rounded bg-opacity-30" />
            ))}
          </div>
        </div>
      )}
    </main>
  );
});

export default MetaData;
